/**
 * The audit — the thing the product is for.
 *
 * Every call carries the whole audit back, including a freshly computed running score and
 * the list of everything still standing between it and being submitted. That is more bytes
 * than a patch would be, and it is the right trade: the screen never has to work out what
 * changed, and an auditor is never shown a total that is one answer out of date.
 */
import { request } from './client';
import type { Problem } from './problem';
import type { AnswerType, CriticalScope, NotApplicableScope, QuestionOption, RequirementTrigger, ScoringModel } from './types';

export type AuditStatus = 'Draft' | 'Submitted' | 'Appealed' | 'Closed';

export type ExclusionReason =
  | 'None'
  | 'MarkedNotApplicable'
  | 'StructurallyNotApplicable'
  | 'Hidden'
  | 'NotScored';

export interface AuditPhoto {
  id: string;
  contentType: string | null;
  sizeInBytes: number | null;
  capturedAt: string;
  /** Short-lived. Minted on every read, never stored. */
  url: string | null;
  /** When url stops working. Absent from a server, or a cached audit, older than the field. */
  urlExpiresAt?: string | null;
}

export interface AuditAnswer {
  questionId: string;
  yesNo: boolean | null;
  scaleValue: number | null;
  selectedOptionIds: string[];
  text: string | null;
  markedNotApplicable: boolean;
  structurallyNotApplicable: boolean;
  comment: string | null;
  answeredAt: string | null;
  photos: AuditPhoto[];
}

export interface AuditSummary {
  id: string;
  reference: string | null;
  locationId: string;
  locationCode: string;
  locationName: string;
  auditorUserId: string;
  auditorName: string;
  formCode: string;
  formVersion: number;
  formName: string;
  status: AuditStatus;
  startedAt: string;
  completedAt: string | null;
  submittedAt: string | null;
  isScored: boolean;
  score: number | null;
  zeroedByCritical: boolean;
  band: string | null;
  bandColour: string | null;
}

export interface QuestionScore {
  questionId: string;
  weight: number;
  exclusion: ExclusionReason;
  ratio: number | null;
  failedCritical: boolean;
  counted: boolean;
}

export interface CategoryScore {
  categoryId: string;
  name: string;
  declaredWeight: number;
  weightInForm: number;
  effectiveWeight: number;
  score: number | null;
  zeroedByCritical: boolean;
  questions: QuestionScore[];
}

export interface ScoringResult {
  isScored: boolean;
  isComplete: boolean;
  score: number | null;
  zeroedByCritical: boolean;
  categories: CategoryScore[];
  unansweredQuestionIds: string[];
  nothingToScore: boolean;
}

export interface Signature {
  signedByName: string;
  signedAt: string;
  /** Short-lived, like a photograph's. */
  url: string | null;
}

export interface AuditDetail {
  summary: AuditSummary;
  /** The snapshot verbatim. Parsed by the client, never rebuilt by it. */
  formSnapshot: string;
  formSnapshotHash: string;
  answers: AuditAnswer[];
  notes: string | null;
  running: ScoringResult | null;
  problems: Problem[];

  signature: Signature | null;
  /** From the snapshot, so it is what the form asked for on the day it was published. */
  requiresSignature: boolean;

  /**
   * Whether the auditor is shown what each question is worth, and the running and
   * category figures, while they work. From the tenant's parameters and read fresh on
   * every request — not from the snapshot, because these decide what a screen draws
   * rather than what a number means.
   */
  showQuestionPoints: boolean;
  showRunningScore: boolean;
}

/** What a snapshot says, as the screen reads it. Mirrors FormSnapshotFormat. */
export interface SnapshotQuestion {
  id: string;
  code: string;
  text: string;
  weight: number;
  type: AnswerType;
  guidance: string | null;
  critical: CriticalScope;
  comment: RequirementTrigger;
  photo: RequirementTrigger;
  notApplicable: { allowed: boolean; scope: NotApplicableScope };
  scale: { min: number; max: number } | null;
  options: Pick<QuestionOption, 'id' | 'text' | 'score'>[] | null;
  condition: { questionId: string; answer: boolean } | null;
}

export interface SnapshotCategory {
  id: string;
  name: string;
  weight: number;
  guidance: string | null;
  questions: SnapshotQuestion[];
}

export interface SnapshotForm {
  schema: number;
  id: string;
  code: string;
  version: number;
  name: string;
  description: string | null;
  scored: boolean;

  /**
   * How this form's numbers are meant to be read. Absent in documents written before
   * schema 6, which were all relative weights — so the reader fills that in rather than
   * leaving it undefined for the screen to guess at.
   */
  scoring: ScoringModel;

  /** What the points add up to under points. Null under weights, and in older documents. */
  pointsTarget: number | null;
  publishedAt: string;
  categories: SnapshotCategory[];
}

/**
 * The wire names in the snapshot are not the C# member names — deliberately, so that
 * renaming a member cannot change what every device receives. This is the other half of
 * that bargain, and it has to stay in step with FormSnapshotFormat.Name.
 */
const ANSWER_TYPES: Record<string, AnswerType> = {
  yesNo: 'YesNo',
  scale: 'Scale',
  singleChoice: 'SingleChoice',
  multipleChoice: 'MultipleChoice',
  text: 'Text',
};

const CRITICAL: Record<string, CriticalScope> = {
  none: 'None',
  category: 'Category',
  form: 'Form',
};

const TRIGGERS: Record<string, RequirementTrigger> = {
  never: 'Never',
  whenNegative: 'WhenNegative',
  always: 'Always',
};

const NA_SCOPES: Record<string, NotApplicableScope> = {
  category: 'Category',
  form: 'Form',
};

const SCORING: Record<string, ScoringModel> = {
  weight: 'Weight',
  points: 'Points',
};

/**
 * Reads the snapshot into the shapes the screen uses.
 *
 * Refuses an unknown spelling rather than guessing. A question whose type nobody recognises
 * would render as nothing and be left unanswered, and an auditor would find out at the end
 * of a visit instead of the start.
 */
export function readSnapshot(content: string): SnapshotForm {
  const raw = JSON.parse(content);

  const look = <T,>(table: Record<string, T>, value: string, what: string): T => {
    const found = table[value];
    if (found === undefined) throw new Error(`Snapshot uses an unknown ${what}: ${value}`);
    return found;
  };

  return {
    ...raw,

    // Absent before schema 6, and the only honest reading of a document that does not say
    // is what every form written then was.
    scoring: raw.scoring === undefined ? 'Weight' : look(SCORING, raw.scoring, 'scoring model'),
    pointsTarget: raw.pointsTarget ?? null,
    categories: raw.categories.map((category: Record<string, unknown>) => ({
      ...category,
      questions: (category.questions as Record<string, never>[]).map((q) => ({
        ...q,
        type: look(ANSWER_TYPES, q.type as unknown as string, 'answer type'),
        critical: look(CRITICAL, q.critical as unknown as string, 'critical scope'),
        comment: look(TRIGGERS, q.comment as unknown as string, 'comment requirement'),
        photo: look(TRIGGERS, q.photo as unknown as string, 'photo requirement'),
        notApplicable: {
          allowed: (q.notApplicable as unknown as { allowed: boolean }).allowed,
          scope: look(
            NA_SCOPES,
            (q.notApplicable as unknown as { scope: string }).scope,
            'not-applicable scope',
          ),
        },
      })),
    })),
  };
}

export interface SaveAnswer {
  yesNo?: boolean | null;
  scaleValue?: number | null;
  selectedOptionIds?: string[] | null;
  text?: string | null;
  notApplicable?: boolean;
  clear?: boolean;
  comment?: string | null;
  answeredAt?: string | null;
}

const base = '/api/v1/audits';
const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

/**
 * A store this auditor may open an audit at.
 *
 * Three fields, not the twenty the organisation screen needs: a name to show and an
 * identifier to send.
 */
export interface AuditableLocation {
  id: string;
  code: string;
  name: string;
}

export const audits = {
  /**
   * The stores in reach, open for business.
   *
   * Not the organisation's list of stores. That one is every store there is, which is what
   * somebody maintaining the register needs and the opposite of what this screen does.
   */
  locations: () => request<AuditableLocation[]>(`${base}/locations`),

  list: (params: { mine?: boolean; status?: AuditStatus; locationId?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.mine) query.set('mine', 'true');
    if (params.status) query.set('status', params.status);
    if (params.locationId) query.set('locationId', params.locationId);
    const suffix = query.toString();
    return request<AuditSummary[]>(`${base}${suffix ? `?${suffix}` : ''}`);
  },

  get: (id: string) => request<AuditDetail>(`${base}/${id}`),

  start: (locationId: string, formTemplateId: string) =>
    request<AuditDetail>(base, json('POST', { locationId, formTemplateId })),

  answer: (id: string, questionId: string, body: SaveAnswer) =>
    request<AuditDetail>(`${base}/${id}/answers/${questionId}`, json('PUT', body)),

  notes: (id: string, notes: string | null) =>
    request<AuditDetail>(`${base}/${id}/notes`, json('PUT', { notes })),

  submit: (id: string) => request<AuditDetail>(`${base}/${id}/submit`, json('POST', {})),

  /**
   * The visit as a PDF.
   *
   * <p>Returns a link that expires, not the file: the server draws it once and keeps it, so
   * asking twice costs nothing and everybody who opens it is looking at the same bytes.</p>
   */
  report: (id: string) => request<{ url: string }>(`${base}/${id}/report`),

  /**
   * Multipart, so no Content-Type header: the browser sets it with the boundary, and
   * naming it ourselves produces a body the server cannot parse.
   */
  addPhoto: (id: string, questionId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<AuditDetail>(`${base}/${id}/answers/${questionId}/photos`, {
      method: 'POST',
      body: form,
    });
  },

  removePhoto: (id: string, photoId: string) =>
    request<AuditDetail>(`${base}/${id}/photos/${photoId}`, { method: 'DELETE' }),

  sign: (id: string, png: Blob, name: string) => {
    const form = new FormData();
    form.append('file', new File([png], 'signature.png', { type: 'image/png' }));
    form.append('name', name);
    return request<AuditDetail>(`${base}/${id}/signature`, { method: 'POST', body: form });
  },

  removeSignature: (id: string) =>
    request<AuditDetail>(`${base}/${id}/signature`, { method: 'DELETE' }),

  /**
   * Throws away a draft nobody has seen.
   *
   * <p>Not queued like the rest: an audit only exists because the server made one, so there
   * is no offline case where a draft is sitting on a device the server has never heard of.
   * Everything else on this screen goes through the outbox precisely because it can happen
   * with no signal; this cannot.</p>
   */
  discard: (id: string) => request<void>(`${base}/${id}`, { method: 'DELETE' }),
};
